export const projectsData = [
  {
    id: "handyman",
    name: "Handyman",
    shortDesc: "Handyman is a web app that connects users to trusted artisans within seconds. The idea was born from the realization that finding reliable help for home repairs isn’t always easy — users often struggle to find skilled artisans they can trust. My goal was to design a seamless experience that makes hiring simple, fast, and trustworthy.",
    year: "2025",
    industry: "Service",
    type: "Web App",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Handyman/handyman1.png?updatedAt=1760880067561",
    coverAlt: "farmwise-cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "Users often struggle to find reliable artisans when they need help with repairs or projects. They rely heavily on word of mouth, which is slow and unreliable. Even when they find one, issues such as trust, poor communication, and inconsistent service quality make the process stressful. During my research, I also noticed another challenge — many users find it difficult to make secure and seamless payments to artisans after the service is completed.",
        image: "https://ik.imagekit.io/pharkman/Handyman/2.png?updatedAt=1760880059453",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "Through surveys and short interviews, I discovered that 8 out of 10 users find it difficult to locate trustworthy artisans quickly. Most said they had at least one bad experience with an unverified artisan, leading to loss of money or poor service. On the artisan side, I found that many lose potential clients due to lack of visibility and poor payment handling. This insight helped me shape the product to not only connect both sides but also make the hiring and payment experience transparent and reliable.",
        image: "https://ik.imagekit.io/pharkman/Handyman/9.png?updatedAt=1760880060265",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "I designed a web app that connects users with verified artisans nearby. Each artisan profile includes ratings, reviews, and previous work samples, helping users make informed decisions. To address the payment challenge, I introduced a secure in-app payment feature — allowing users to pay artisans safely only after confirming job completion. This ensures fairness and builds trust between both parties.",
        image: "https://ik.imagekit.io/pharkman/Handyman/3.png?updatedAt=1760880060505",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "As a concept case study, the project demonstrates how a well-designed platform can make finding and hiring artisans easier, faster, and more trustworthy. If implemented, HandyMan could help users save time, reduce risk, and improve satisfaction when hiring artisans, while also giving artisans more visibility and consistent job opportunities.",
        image: "https://ik.imagekit.io/pharkman/Handyman/Frame%202147224524.png?updatedAt=1760880059583",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Handyman/4.png?updatedAt=1760880060497", alt: "handyman-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Handyman/13.png?updatedAt=1760880072454", alt: "handyman-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Handyman/12.png?updatedAt=1760880069099", alt: "handyman-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Handyman/5.png?updatedAt=1760880060768", alt: "handyman-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Handyman/11.png?updatedAt=1760880060383", alt: "handyman-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Handyman/8.png?updatedAt=1760880061187", alt: "handyman-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Handyman/Frame%202147224520.png?updatedAt=1760880061004", alt: "handyman-ui" },
    ],
    prevProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
    nextProject: { name: "MedicScan", link: "/projects/mediscan" },
  },
{
    id: "tresta",
    name: "Tresta",
    shortDesc: "Tresta is a student marketplace app that allows university students to connect and sell items within their campus community. The idea came from observing how many students struggle to sell or buy items like gadgets, textbooks, and furniture after each semester. Tresta creates a safe and exclusive platform where students can trade conveniently with verified peers.",
    year: "2025",
    industry: "E-commerce",
    type: "Mobile App",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Tresta/iPhone%2016%20Pro%20Office%2003@2x%201.png?updatedAt=1760882025818",
    coverAlt: "tresta-cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "Students often rely on random group chats or social media pages to buy and sell items, which makes the process unorganized and unsafe. Many end up getting scammed or wasting time dealing with fake buyers and sellers. There’s also the issue of outsiders pretending to be students just to exploit the platform. This lack of structure and trust discourages students from trading among themselves.",
        image: "https://ik.imagekit.io/pharkman/Tresta/Frame%202147224113.png?updatedAt=1760882029480",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "Through conversations and surveys with students from different universities, I discovered that most students wanted a simpler and safer way to sell their items without leaving their campus network. They wanted assurance that every buyer and seller was a verified student and that transactions were genuine. This insight shaped the foundation of Tresta — a marketplace that prioritizes trust, verification, and ease of use.",
        image: "https://ik.imagekit.io/pharkman/Tresta/Frame%202147224115.png?updatedAt=1760882018384",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "I designed Tresta to solve these problems by making campus trading safe, fast, and personal. Students sign up using their school ID for verification, ensuring that only real students can access the marketplace. The platform includes categorized listings, a simple chat system for negotiation, and a clean interface that makes browsing and posting items effortless. Every design decision focused on building trust and creating a familiar, student-friendly experience.",
        image: "https://ik.imagekit.io/pharkman/Tresta/Frame%202147224114.png?updatedAt=1760882018749",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "Designing Tresta helped me explore how digital products can solve real campus problems through trust and simplicity. The final concept presents a seamless trading experience built around verified student identities and community-driven interaction. It reflects how thoughtful design can transform casual student exchanges into a safe, structured ecosystem that encourages connection, sustainability, and confidence in peer-to-peer trading.",
        image: "https://ik.imagekit.io/pharkman/Tresta/Frame%202147224116.png?updatedAt=1760882018609",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%201.png?updatedAt=1760882035194", alt: "tresta-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%204.png?updatedAt=1760882030822", alt: "tresta-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%205.png?updatedAt=1760882031963", alt: "tresta-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Tresta/Frame%202147224117.png?updatedAt=1760882022162", alt: "tresta-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%202.png?updatedAt=1760882034376", alt: "tresta-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%208.png?updatedAt=1760882035499", alt: "tresta-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Tresta/Mockup%207.png?updatedAt=1760882029741", alt: "tresta-ui" },
    ],
    prevProject: { name: "FarmWise", link: "/projects/farmwise" },
    nextProject: { name: "AvaLaundry", link: "/projects/ava-laundry" },
  },
 {
    id: "beam",
    name: "Beam",
    shortDesc: "Privacy has always been one of blockchain’s biggest promises, yet every transaction remains visible for anyone to trace. Beam was created to change that. It’s a non-custodial web app that allows people to send, receive, and manage digital assets privately using zero-knowledge technology. Since its launch, Beam has processed over $750,000 in transactions, showing how privacy and usability can truly coexist in modern finance.",
    year: "2025",
    industry: "web3",
    type: "Web app",
    link: "https://trybeam.xyz/",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%20Ribbon%206.png?updatedAt=1761038475058",
    coverAlt: "Beam Cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "Blockchain gives users freedom and transparency, but it often comes at the cost of privacy. Every wallet address and transaction detail can be tracked publicly, which makes many people hesitant to use blockchain for everyday financial activities. The goal was to design an experience that keeps users fully in control of their funds while ensuring their financial activity remains private and easy to manage.",
        image: "https://ik.imagekit.io/pharkman/Beam%201/260_1x_shots_so.png?updatedAt=1760882112209",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "From the product team’s research, it became clear that many blockchain users still struggled with privacy and ease of use. Most people wanted a way to handle transactions confidently without worrying about exposure or technical complexity. These insights shaped Beam’s approach — focusing on trust, simplicity, and education, helping users understand how privacy can be built directly into their experience without adding friction.",
        image: "https://ik.imagekit.io/pharkman/Beam%201/808_1x_shots_so.png?updatedAt=1760882106966",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "Beam empowers users to send and receive payments, on-ramp and off-ramp fiat, and manage virtual accounts in one private, non-custodial platform. The design focuses on clarity and comfort. Each flow was built to feel familiar, with friendly microinteractions and intuitive layouts that remove the intimidation often associated with blockchain products. A virtual card feature is also underway, allowing users to make private, direct transactions using their Beam balance seamlessly.",
        image: "https://ik.imagekit.io/pharkman/Beam%201/136_1x_shots_so.png?updatedAt=1760882124714",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "Beam has successfully processed over $750,000 in transactions, helping users take back control of their privacy while keeping the experience simple and secure. This project deepened my understanding of designing for trust — showing that privacy doesn’t have to feel hidden or complex, it can be natural, empowering, and human.",
        image: "https://ik.imagekit.io/pharkman/Beam%201/873_1x_shots_so.png?updatedAt=1760882112668",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Beam%201/898_1x_shots_so.png?updatedAt=1760882113203", alt: "beam-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%205.png?updatedAt=1760882128669", alt: "beam-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%204.png?updatedAt=1760882126828", alt: "beam-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Beam%201/710_1x_shots_so.png?updatedAt=1760882125009", alt: "beam-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%201.png?updatedAt=1760882126981", alt: "beam-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%2010.png?updatedAt=1760882125737", alt: "beam-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Beam%201/877_1x_shots_so.png?updatedAt=1760882110991", alt: "beam-ui" },
    ],
    prevProject: { name: "MedicScan", link: "/projects/mediscan" },
    nextProject: { name: "AvaPay", link: "/projects/avapay" },
  },
    {
    id: "paypro",
    name: "Paypro",
    shortDesc: "PayPro was built to simplify how freelancers and business owners get paid. The idea came from a simple observation — getting paid shouldn’t feel like a full-time job. Between delayed transfers, confusing platforms, and high fees, most people were tired of juggling multiple tools just to receive money from clients or customers.",
    year: "2024",
    industry: "Fintech",
    type: "Mobile App",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Paypro/Mockup%201.png?updatedAt=1760882436300",
    coverAlt: "paypro-cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "For freelancers and small business owners, getting paid is often the hardest part of the job. Traditional banks move slowly. Many digital platforms don’t support local currencies or charge high international fees. On top of that, users have to jump between apps to handle simple financial tasks. The result? Missed opportunities, payment stress, and wasted time that could be better spent growing their work or business.",
        image: "https://ik.imagekit.io/pharkman/Paypro/Mockup%204.png?updatedAt=1760882431589",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "Through user interviews and feedback sessions, I discovered that many people struggled with fragmented payment experiences. Freelancers and business owners often had to switch between multiple apps to send, receive, or withdraw funds. Users wanted a single, secure platform that could handle everything — fast transfers, easy conversions, and an interface that felt effortless. These insights guided how PayPro was designed to bring simplicity and trust to digital payments.",
        image: "https://ik.imagekit.io/pharkman/Paypro/Frame%202147223397.png?updatedAt=1760882429744",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "PayPro was designed to feel effortless from the first tap. The interface focuses on clarity, guiding users through every action without confusion. Sending or receiving money happens in seconds. The on-ramp and off-ramp features bridge fiat and digital payments, while the upcoming virtual card expands how users can spend and manage funds. The product balances flexibility for freelancers and business owners with simplicity that makes it approachable to anyone. Every screen was built with speed, security, and transparency in mind.",
        image: "https://ik.imagekit.io/pharkman/Paypro/Frame%203037.png?updatedAt=1760882427905",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "PayPro is quickly becoming a trusted payment platform for freelancers and business owners who want a smarter, simpler way to handle transactions. Early users highlight how clean the experience feels — no clutter, no delays, just smooth financial flow.",
        image: "https://ik.imagekit.io/pharkman/Paypro/Frame%202147223398.png?updatedAt=1760882430090",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Paypro/Frame%202147223402.png?updatedAt=1760882428946", alt: "paypro-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Paypro/Mockup%208.png?updatedAt=1760882436281" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Paypro/Mockup%203.png?updatedAt=1760882433240" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Paypro/Frame%202147223404.png?updatedAt=1760882430652", alt: "paypro-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Paypro/Mockup%202.png?updatedAt=1760882432988", alt: "paypro-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Paypro/Mockup%202-1.png?updatedAt=1760882433194", alt: "paypro-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Paypro/Frame%202147223403.png?updatedAt=1760882436953", alt: "paypro-ui" },
    ],
    prevProject: { name: "AvaLaundry", link: "/projects/ava-laundry" },
    nextProject: { name: "MatchSingle", link: "/projects/matchsingle" }, // Updated to matchsingle
  },
  {
    id: "maiddy",
    name: "Maiddy",
    shortDesc: "Maiddy Chamber is a modern collaboration app designed to bring calm and clarity to teamwork. It combines chat, meetings, and project management into one shared space, helping teams communicate, organize, and stay aligned without juggling multiple tools. The goal was to build something that feels light, human, and smart — not another cluttered productivity dashboard.",
    year: "2025",
    industry: "SASS",
    type: "Web App",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2026.png?updatedAt=1760882821272",
    coverAlt: "Cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "While researching how teams work remotely, one thing became clear: communication and project management felt fragmented. People had to move between Slack for chats, Zoom for meetings, and Trello or Notion for task tracking. The result was constant switching, scattered context, and lost focus. Even small teams struggled to maintain visibility across conversations, tasks, and schedules. Maiddy was born from the need to unify these experiences into one simple, connected platform.",
        image: "https://ik.imagekit.io/pharkman/Maiddy/576_1x_shots_so.png?updatedAt=1760882806392",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "We spoke with team leads, designers, and startup founders to understand their daily frustrations. Many expressed that their workflow had become too tool-dependent, and this fragmentation slowed collaboration. Teams wanted something that worked as naturally as a conversation — a place where discussions, actions, and outcomes lived together. Another key insight was emotional fatigue: people didn’t just want tools that managed work; they wanted tools that cared about how they worked. This shaped our decision to build features like wellness monitoring and AI-powered assistance.",
        image: "https://ik.imagekit.io/pharkman/Maiddy/29_1x_shots_so.png?updatedAt=1760882809257",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "Maiddy Chamber is an intelligent workspace that unites chat, tasks, and meetings in one flow. The chat supports direct messages and channels, powered by AI that can summarize conversations, translate instantly, and schedule meetings with ease. A simple Kanban board keeps task management clear and lightweight, while the built-in meeting hub lets teams plan and review without leaving the space. Every interaction was designed to feel fluid, cohesive, and effortless — making collaboration more natural and human.",
        image: "https://ik.imagekit.io/pharkman/Maiddy/878_1x_shots_so.png?updatedAt=1760882813587",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "The result is a calm, intelligent collaboration platform that simplifies teamwork without stripping away its human side. Teams can now hold conversations, plan tasks, and manage meetings in one space — staying productive without burnout. Maiddy Chamber redefines team collaboration as something seamless, smart, and emotionally aware.",
        image: "https://ik.imagekit.io/pharkman/Maiddy/395_1x_shots_so.png?updatedAt=1760882809708",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Maiddy/637_1x_shots_so.png?updatedAt=1760882806619", alt: "maiddy-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2014.png?updatedAt=1760882820355", alt: "maiddy-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2015.png?updatedAt=1760882818206", alt: "maiddy-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Maiddy/736_1x_shots_so.png?updatedAt=1760882808535", alt: "maiddy-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2029.png?updatedAt=1760882819989", alt: "maiddy-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2027.png?updatedAt=1760882820372", alt: "maiddy-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Maiddy/638_1x_shots_so.png?updatedAt=1760882812175", alt: "maiddy-ui" },
    ],
    prevProject: { name: "MatchSingle", link: "/projects/matchsingle" }, // Updated to matchsingle
    nextProject: { name: "FarmWise", link: "/projects/farmwise" },
  },
  {
    id: "gidipitch",
    name: "Gidiptich",
    shortDesc: "GidiPitch is a platform built to speed up and simplify how founders in Africa prepare vital startup materials. Whether it’s a pitch deck, resume, or startup summary, the goal is to reduce the friction in going from idea to presentation — especially in environments where resources and time are limited.",
    year: "2025",
    industry: "SASS",
    type: "Web App",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%20Ribbon%2011.png?updatedAt=1760883180637",
    coverAlt: "cover",
    sections: [
      {
        type: "problem-statement",
        title: "Problem Statement",
        text: "Many founders struggle with limited access to design and strategy expertise. They spend too much energy on formatting, structure, and aesthetics rather than focusing on the idea and narrative. The result: presentations that don’t get the message across clearly, or delays from having to outsource or start from scratch.",
        image: "https://ik.imagekit.io/pharkman/Gidipitch/3_1x_shots_so.png?updatedAt=1760883181382",
        alt: "problem",
      },
      {
        type: "problem-statement",
        title: "Research & Insights",
        text: "Through conversations with founders, incubators, and startup mentors, we learned that most founders waste valuable time formatting slides instead of refining ideas. Many find presentation design intimidating and wish for tools that understand their unique market realities. The insight was clear — they needed a tool that felt local, intuitive, and smart enough to do the heavy lifting.",
        image: "https://ik.imagekit.io/pharkman/Gidipitch/173_1x_shots_so.png?updatedAt=1760883169414",
        alt: "research",
      },
      {
        type: "problem-statement",
        title: "Solution",
        text: "GidiPitch was designed as a smart, adaptive toolkit that combines AI, clean templates, and simple UX. The system generates customized decks and resumes based on founder input, helps craft persuasive narratives, and suggests localized examples relevant to African markets. Every interaction was built to remove friction — from writing to layout — so founders can focus on building and pitching.",
        image: "https://ik.imagekit.io/pharkman/Gidipitch/57_1x_shots_so.png?updatedAt=1760883169787",
        alt: "solution",
      },
      {
        type: "problem-statement",
        title: "Outcome",
        text: "GidiPitch empowers founders to create polished investor materials in hours, not days. It reduces outsourcing costs, boosts confidence, and gives startups a professional edge when pitching. Beyond design, it helps them tell their stories in a way that feels authentic, impactful, and ready for investors.",
        image: "https://ik.imagekit.io/pharkman/Gidipitch/838_1x_shots_so.png?updatedAt=1760883163776",
        alt: "outcome",
      },
    ],
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Gidipitch/665_1x_shots_so.png?updatedAt=1760883176387", alt: "matchsingle-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%204.png?updatedAt=1760883185060", alt: "gidipitch-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%203.png?updatedAt=1760883195839", alt: "gidipitch-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Gidipitch/115_1x_shots_so.png?updatedAt=1760883173047", alt: "gidipitch-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%2020.png?updatedAt=1760883195891", alt: "gidipitch-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%2019.png?updatedAt=1760883190947", alt: "gidipitch-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Gidipitch/564_1x_shots_so.png?updatedAt=1760883171789", alt: "gadgetfix-ui" },
    ],
    prevProject: { name: "AvaPay", link: "/projects/avapay" },
    nextProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
  },
  {
    id: "zooma",
    name: "Zooma",
    shortDesc: "Zooma is a transportation platform that connects users with reliable rides, offering a seamless and engaging experience for urban commuting.",
    year: "2025",
    industry: "Transportation",
    type: "Website",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%2025.png?updatedAt=1760883354759",
    coverAlt: "zooma-cover",
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Zooma%201/844_1x_shots_so.png?updatedAt=1760883354645", alt: "zooma-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%2017.png?updatedAt=1760883354972", alt: "zooma-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%2022.png?updatedAt=1760883362801", alt: "zooma-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Zooma%201/597_1x_shots_so.png?updatedAt=1760883351713", alt: "zooma-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%2012.png?updatedAt=1760883354049", alt: "zooma-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%204.png?updatedAt=1760883349927", alt: "zooma-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Zooma%201/zooma%20Prototype.mp4", alt: "zooma-ui" },
    ],
    prevProject: { name: "AvaPay", link: "/projects/avapay" },
    nextProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
  },
  {
    id: "wellivo",
    name: "Wellivo",
    shortDesc: "Wellivo is a health platform that helps users track wellness goals and access personalized resources for better living.",
    year: "2025",
    industry: "Health",
    type: "Website",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%20Ribbon%2012.png?updatedAt=1760938247519",
    coverAlt: "wellivo-cover",
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Wellivo/345_1x_shots_so.png?updatedAt=1760938240915", alt: "wellivo-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%206.png?updatedAt=1760938247542", alt: "wellivo-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%2015.png?updatedAt=1760938244301", alt: "wellivo-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Wellivo/123_1x_shots_so.png?updatedAt=1760938240569", alt: "wellivo-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%2018.png?updatedAt=1760938248314", alt: "wellivo-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%2027.png?updatedAt=1760938246020", alt: "wellivo-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Wellivo/Wellivo-1.mp4" },
    ],
    prevProject: { name: "AvaPay", link: "/projects/avapay" },
    nextProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
  },
  {
    id: "handyman-web",
    name: "Handyman",
    shortDesc: "Handyman is a web app that connects users to trusted artisans within seconds. The idea was born from the realization that finding reliable help for home repairs isn’t always easy — users often struggle to find skilled artisans they can trust. My goal was to design a seamless experience that makes hiring simple, fast, and trustworthy.",
    year: "2025",
    industry: "Service",
    type: "Website",
    roles: ["UI Design", "UX Research", "UX Design", "Prototype"],
    coverImage: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%2018.png?updatedAt=1760938291142",
    coverAlt: "handyman-cover",
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Handyman%20web/381_1x_shots_so.png?updatedAt=1760938282545", alt: "handyman-ui" },
      {
        class: "UI-2",
        images: [
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%208.png?updatedAt=1760938291624", alt: "handyman-ui" },
          { class: "UI-2-1", image: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%204.png?updatedAt=1760938285217", alt: "handyman-ui" },
        ],
      },
      { class: "UI-3", image: "https://ik.imagekit.io/pharkman/Handyman%20web/414_1x_shots_so.png?updatedAt=1760938279963", alt: "handyman-ui" },
      {
        class: "UI-4",
        images: [
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%2019.png?updatedAt=1760938290385", alt: "handyman-ui" },
          { class: "UI-4-1", image: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%2027.png?updatedAt=1760938289970", alt: "handyman-ui" },
        ],
      },
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Handyman%20web/794_1x_shots_so.png?updatedAt=1760938284126", alt: "handyman-ui" },
      
      { class: "UI-5", image: "https://ik.imagekit.io/pharkman/Handyman%20web/60_1x_shots_so.png?updatedAt=1760938282838", alt: "handyman-ui" },
    ],
    prevProject: { name: "AvaPay", link: "/projects/avapay" },
    nextProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
  },
  {
    id: "decklo",
    name: "Decklo",
    shortDesc: "Decklo is a brand built to help founders and professionals present themselves with confidence. The platform offers practical tools like a pitch deck generator, resume builder, AI practice coach for presentations, and business proposal creation. The brand identity reflects that mission: clean, direct, and focused on turning ideas and profiles into polished, investor-ready or employer-ready assets. My goal with the logo was to capture that sense of clarity and forward movement—creating a mark that feels modern, reliable, and built for ambitious users who need to make a strong impression.",
    year: "2025",
    industry: "SASS",
    type: "Branding",
    roles: ["Brand Identity", "Visual Direction", "Visual Resaerch", "Branding"],
    coverImage: "https://ik.imagekit.io/pharkman/Decklo/Slide%2016_9%20-%2037.png",
    coverAlt: "handyman-cover",
    uiScreens: [
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228528.png?updatedAt=1763166481091", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228529.png?updatedAt=1763166481337", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228530.png?updatedAt=1763166480465", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228531.png?updatedAt=1763166480647", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228532.png?updatedAt=1763166480925", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228535.png?updatedAt=1763166480871", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228536.png?updatedAt=1763166481004", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228537.png?updatedAt=1763166481108", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228538.png?updatedAt=1763166488857", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228539.png?updatedAt=1763166508152", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228540.png?updatedAt=1763166518443", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228541.png?updatedAt=1763166494152", alt: "handyman-ui" },

      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228542.png?updatedAt=1763166520670", alt: "handyman-ui" },
      { class: "UI-1", image: "https://ik.imagekit.io/pharkman/Decklo/Frame%202147228543.png?updatedAt=1763166480855", alt: "handyman-ui" },

    ],
    prevProject: { name: "AvaPay", link: "/projects/avapay" },
    nextProject: { name: "GadgetFix", link: "/projects/gadgetfix" },
  },
];

export const moreProjects = [
  {
    thumbnailClass: "project-1",
    projectIndustry: "Service",
    projectType: "Web App",
    projectYear: "2025",
    projectName: "Handyman",
    link: "/projects/handyman",
    section: "Feature Work",
    id: "handyman",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Handyman/handyman1.png?updatedAt=1760880067561",
  },
  {
    thumbnailClass: "project-2",
    projectIndustry: "E-commerce",
    projectType: "Mobile App",
    projectYear: "2025",
    projectName: "Tresta",
    link: "/projects/tresta",
    section: "Feature Work",
    id: "tresta",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Tresta/iPhone%2016%20Pro%20Office%2003@2x%201.png?updatedAt=1760882025818",
  },
  {
    thumbnailClass: "project-3",
    projectIndustry: "Web3",
    projectType: "Web App",
    projectYear: "2025",
    projectName: "Beam",
    link: "/projects/beam",
    section: "Feature Work",
    id: "beam",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Beam%201/Mockup%20Ribbon%206.png?updatedAt=1761038475058",
  },
  {
    thumbnailClass: "project-4",
    projectIndustry: "Fintech",
    projectType: "Mobile App",
    projectYear: "2024",
    projectName: "Paypro",
    link: "/projects/paypro",
    section: "Feature Work",
    id: "paypro",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Paypro/Mockup%201.png?updatedAt=1760882436300",
  },
    {
    thumbnailClass: "project-5",
    projectIndustry: "SASS",
    projectType: "Branding",
    projectYear: "2025",
    projectName: "Decklo",
    link: "/projects/decklo",
    section: "Feature Work",
    id: "decklo",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Decklo/Slide%2016_9%20-%2037.png",
  },
  {
    thumbnailClass: "project-6",
    projectIndustry: "SASS",
    projectType: "Web App",
    projectYear: "2025",
    projectName: "Gidipitch",
    link: "/projects/gidipitch",
    section: "Feature Work",
    id: "gidipitch",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Gidipitch/Mockup%20Ribbon%2011.png?updatedAt=1760883180637",
  },
  {
    thumbnailClass: "project-7",
    projectIndustry: "Logistic",
    projectType: "Website",
    projectYear: "2025",
    projectName: "Zooma",
    link: "/projects/zooma",
    section: "Feature Work",
    id: "zooma",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Zooma%201/Mockup%2025.png?updatedAt=1760883354759",
  },
  {
    thumbnailClass: "project-8",
    projectIndustry: "Health",
    projectType: "Website",
    projectYear: "2025",
    projectName: "Wellivo",
    link: "/projects/wellivo",
    section: "Feature Work",
    id: "wellivo",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Wellivo/Mockup%20Ribbon%2012.png?updatedAt=1760938247519",
  },
  {
    thumbnailClass: "project-9",
    projectIndustry: "Health",
    projectType: "Website",
    projectYear: "2025",
    projectName: "Handyman Web",
    link: "/projects/handyman-web",
    section: "Feature Work",
    id: "handyman-web",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Handyman%20web/Mockup%2018.png?updatedAt=1760938291142",
  },

  {
    thumbnailClass: "project-10",
    projectIndustry: "Ui Deign",
    projectType: "Website",
    projectYear: "2025",
    projectName: "Gamification",
    link: "https://www.dribbble.com/fitfly",
    section: "UI Shot",
    id: "payora",
    target: "_blank",
    imageSrc: "https://ik.imagekit.io/pharkman/General%20Asset/game%201.png",
  },
  {
    thumbnailClass: "project-11",
    projectIndustry: "Web3",
    projectType: "Mobile App",
    projectYear: "2025",
    projectName: "LumenPay",
    link: "https://www.dribbble.com/fitfly",
    section: "UI Shot",
    id: "lumenpay",
    target: "_blank",
    imageSrc: "https://ik.imagekit.io/pharkman/General%20Asset/980_1x_shots_so.png",
  },
  {
    thumbnailClass: "project-12",
    projectIndustry: "SASS",
    projectType: "Website",
    projectYear: "2025",
    projectName: "MemoAi",
    link: "https://www.dribbble.com/fitfly",
    section: "UI Shot",
    id: "memoai",
    target: "_blank",
    imageSrc: "https://ik.imagekit.io/pharkman/General%20Asset/memoai.png",
  },
  {
    thumbnailClass: "project-13",
    projectIndustry: "Art",
    projectType: "Illustration",
    projectYear: "2025",
    projectName: "Illustration Pack",
    link: "https://www.dribbble.com/fitfly",
    section: "UI Shot",
    id: "illustration",
    target: "_blank",
    imageSrc: "https://ik.imagekit.io/pharkman/General%20Asset/Frame%202147228558%201.png?updatedAt=1762594257525",
  },
   {
    thumbnailClass: "project-14",
    projectIndustry: "SASS",
    projectType: "Web App",
    projectYear: "2025",
    projectName: "Maiddy",
    link: "/projects/maiddy",
    section: "Feature Work",
    id: "maiddy",
    target: "_self",
    imageSrc: "https://ik.imagekit.io/pharkman/Maiddy/Mockup%2026.png?updatedAt=1760882821272",
  },
];